// SPDX-License-Identifier: AGPL-3.0-or-later

import '@app/features/daoi/components/DaoI.css';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import Authentication from '@app/features/auth/state/Authentication';
import {DaoIWalletGate} from '@app/features/daoi/components/DaoIWalletGate';
import SessionManager from '@app/features/platform/state/AuthSession';
import {instanceRequest, instanceTargetFromSnapshot} from '@app/features/platform/transport/InstanceHTTP';
import Theme from '@app/features/theme/state/Theme';
import {Button} from '@app/features/ui/button/Button';
import {ThemeTypes} from '@fluxer/constants/src/UserConstants';
import {type ReactNode, useEffect, useState} from 'react';

// This gate runs before Gateway READY. New accounts cannot initialize a chat
// runtime until their wallet is linked, so an AppLayout-only gate deadlocks.
export function useDaoIWalletAdmission(standalone: boolean): ReactNode | null {
	const token = SessionManager.token;
	const snapshot = RuntimeConfig.getSnapshotOrNull();
	const endpoint = snapshot?.apiEndpoint;
	const applicable =
		Authentication.isAuthenticated &&
		!standalone &&
		RuntimeConfig.usesUsernameSignIn &&
		snapshot?.sso?.enabled === true;
	const [result, setResult] = useState<{
		token: string | null;
		endpoint?: string;
		linked: boolean;
		error?: boolean;
	} | null>(null);
	const [retry, setRetry] = useState(0);
	useEffect(() => {
		if (!applicable || !snapshot) {
			setResult(null);
			return;
		}
		const controller = new AbortController();
		void instanceRequest<{required: boolean; linked: boolean}>({
			method: 'GET',
			path: '/auth/sso/wallet/status',
			target: instanceTargetFromSnapshot(snapshot),
			auth: 'session',
			signal: controller.signal,
		})
			.then((response) => {
				if (!controller.signal.aborted)
					setResult({token, endpoint, linked: !response.body.required || response.body.linked});
			})
			.catch(() => {
				if (!controller.signal.aborted) setResult({token, endpoint, linked: false, error: true});
			});
		return () => controller.abort();
	}, [applicable, token, endpoint, retry]);
	if (!applicable) return null;
	if (result?.token === token && result.endpoint === endpoint && result.linked) return null;
	const checking = result?.token !== token || result?.endpoint !== endpoint;
	return (
		<div
			className="daoi-app daoi-admission"
			data-daoi-theme={Theme.effectiveTheme === ThemeTypes.LIGHT ? 'light' : 'dark'}
			data-flx="daoi.dao-i-wallet-admission.use-dao-i-wallet-admission.daoi-app"
		>
			{checking ? (
				<div
					className="daoi-state"
					role="status"
					data-flx="daoi.dao-i-wallet-admission.use-dao-i-wallet-admission.daoi-state"
				>
					正在确认账号状态…
				</div>
			) : result?.error ? (
				<div className="daoi-state" data-flx="daoi.dao-i-wallet-admission.use-dao-i-wallet-admission.daoi-state--2">
					<p data-flx="daoi.dao-i-wallet-admission.use-dao-i-wallet-admission.p">暂时无法确认钱包绑定状态。</p>
					<Button
						onClick={() => setRetry((value) => value + 1)}
						data-flx="daoi.dao-i-wallet-admission.use-dao-i-wallet-admission.daoi-button.set-retry"
					>
						重试
					</Button>
				</div>
			) : (
				<DaoIWalletGate
					preRuntime
					data-flx="daoi.dao-i-wallet-admission.use-dao-i-wallet-admission.dao-i-wallet-gate"
				/>
			)}
		</div>
	);
}
